/**
 * Image resolver — resolves _drawing and _pict placeholder nodes to image PMNodes.
 *
 * Port of V1's resolveImages from index.ts. Operates on PMNodes post-conversion.
 * Reuses V1's parseDrawing and parsePict from imageParser.ts.
 */

import type { PMNode, ParseContext } from '../../ooxmlParser/types';
import type JSZip from 'jszip';
import { parseDrawing, parsePict } from '../../ooxmlParser/imageParser';

/**
 * Resolve image placeholders in a flat array of PMNodes.
 * Splits paragraphs around images (images become sibling block nodes).
 */
export async function resolveImages(
  nodes: PMNode[],
  ctx: ParseContext,
  zip: JSZip
): Promise<PMNode[]> {
  const result: PMNode[] = [];

  for (const node of nodes) {
    // Recurse into table cells
    if (node.type === 'table' && node.content) {
      node.content = await resolveImagesInRows(node.content, ctx, zip);
      result.push(node);
      continue;
    }

    // Check if this paragraph/heading contains image placeholders
    if (
      (node.type === 'paragraph' || node.type === 'heading') &&
      node.content
    ) {
      const hasImages = node.content.some(
        (c) => c.type === '_drawing' || c.type === '_pict'
      );

      if (!hasImages) {
        result.push(node);
        continue;
      }

      // Split paragraph around images
      let currentInlineNodes: PMNode[] = [];

      for (const child of node.content) {
        if (child.type === '_drawing' || child.type === '_pict') {
          // Flush accumulated inline content as a paragraph
          if (currentInlineNodes.length > 0) {
            result.push({
              ...node,
              content: currentInlineNodes,
            });
            currentInlineNodes = [];
          }

          // Resolve the image
          const el = child.attrs?._element as Element;
          if (el) {
            const imageNode =
              child.type === '_drawing'
                ? await parseDrawing(el, ctx, zip)
                : await parsePict(el, ctx, zip);
            if (imageNode) {
              result.push(imageNode);
            }
          }
        } else {
          currentInlineNodes.push(child);
        }
      }

      // Flush remaining inline content
      if (currentInlineNodes.length > 0) {
        result.push({
          ...node,
          content: currentInlineNodes,
        });
      }
    } else {
      result.push(node);
    }
  }

  return result;
}

async function resolveImagesInRows(
  rows: PMNode[],
  ctx: ParseContext,
  zip: JSZip
): Promise<PMNode[]> {
  for (const row of rows) {
    if (row.content) {
      for (const cell of row.content) {
        if (cell.content) {
          cell.content = await resolveImages(cell.content, ctx, zip);
        }
      }
    }
  }
  return rows;
}
